import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input } from 'components/FormInputs';
import moment from 'logic/moment';
import { useFormik } from 'components/shared/Formik';

const CancelForm = ({
  isHost,
  disableButtons,
  onCancel,
}) => {
  const intl = useIntl();
  const formik = useFormik({
    initialValues: {
      cancellation_reason: '',
    },
    validate: (values) => {
      const errors = {};

      if (!values.cancellation_reason) {
        errors.cancellation_reason = intl.formatMessage({
          id: 'bookingInfo.cancelReason',
          defaultMessage: 'Please provide a reason for cancelling this booking',
        });
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        await onCancel(values);
      } catch (err) {
        setSubmitting(false);
      }
    },
  });

  const {
    values, errors, handleChange, handleSubmit, isValid, isSubmitting,
  } = formik;

  return (
    <form onSubmit={handleSubmit}>
      <hr />
      <Input
        label={null}
        placeholder={intl.formatMessage({
          id: 'bookingInfo.cancelReason',
          defaultMessage: 'Please provide a reason for cancelling this booking',
        })}
        name="cancellation_reason"
        value={values.cancellation_reason}
        errors={errors}
        onChange={handleChange}
        multiline
        rows={5}
        inputProps={{
          maxLength: 1000,
        }}
      />
      <Button
        variant="contained"
        color="secondary"
        disabled={disableButtons || !isValid || isSubmitting}
        type="submit"
      >
        {
          isHost && (
            <FormattedMessage
              id="bookingInfo.cancelSureHost"
              defaultMessage="I'm sure I want to cancel this offer"
            />
          )
        }
        {
          !isHost && (
            <FormattedMessage
              id="bookingInfo.cancelSureGuest"
              defaultMessage="I'm sure I want to cancel this booking"
            />
          )
        }
      </Button>
    </form>
  );
};

CancelForm.propTypes = {
  isHost: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  disableButtons: PropTypes.bool,
};

const CancelButton = ({
  isHost, booking, disableButtons, onCancel,
}) => {
  const [cancelling, setCancelling] = React.useState(false);
  let refund = null;
  let amount = 0;

  if (moment().isBefore(moment(booking.cancel_for_full))) {
    refund = booking.cancel_for_full;
    amount = 100;
  } else if (moment().isBefore(moment(booking.cancel_for_half))) {
    refund = booking.cancel_for_half;
    amount = 50;
  }

  if (moment(booking.check_in).isBefore(moment())) {
    return null;
  }

  if (!cancelling) {
    return (
      <Box mb={4}>
        <Typography variant="subtitle2">
          <FormattedMessage
            id="bookingForm.cancellation"
            defaultMessage="Cancellation"
          />
        </Typography>
        {
          isHost && (
            <Typography paragraph>
              <FormattedMessage
                id="booking.cancellationExplanation"
                defaultMessage="If you would like to cancel this booking use the button below"
              />
            </Typography>
          )
        }
        {
          !isHost && (
            <>
              {
                !!refund && (
                  <Typography paragraph>
                    <FormattedMessage
                      id="booking.cancellationExplanationGuestRefund"
                      defaultMessage="If you cancel your booking before {date} you will be refunded {amount}% of the amount paid"
                      values={{
                        amount,
                        date: moment(refund).format('LLL'),
                      }}
                    />
                  </Typography>
                )
              }
              {
                !refund && (
                  <Typography paragraph>
                    <FormattedMessage
                      id="booking.cancellationExplanationGuestNoRefund"
                      defaultMessage="If you cancel your booking now you will not receive any refund"
                    />
                  </Typography>
                )
              }
            </>
          )
        }

        <Button
          variant="contained"
          color="secondary"
          disabled={disableButtons}
          onClick={() => setCancelling(true)}
          type="button"
        >
          <CloseIcon />
          {
            isHost && (
              <FormattedMessage
                id="Cancel offer"
                defaultMessage="Cancel offer"
              />
            )
          }
          {
            !isHost && (
              <FormattedMessage
                id="Cancel booking"
                defaultMessage="Cancel booking"
              />
            )
          }
        </Button>
      </Box>
    );
  }

  return (
    <CancelForm {...{ isHost, disableButtons, onCancel }} />
  );
};

CancelButton.propTypes = {
  isHost: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  disableButtons: PropTypes.bool,
  booking: PropTypes.shape({
    cancel_for_full: PropTypes.string.isRequired,
    cancel_for_half: PropTypes.string.isRequired,
    check_in: PropTypes.string.isRequired,
  }).isRequired,
};

export default CancelButton;
